import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import * as Sentry from '@sentry/nextjs';
import { format, isValid } from 'date-fns';

interface FiltersValues {
    [key: string]: unknown;
}

export const getFiltersFromQuery = <D>(conversionFuncs: D): FiltersValues => {
    const queryParams = window.location.search.replace('?', '');
    const allowedFilters = Object.keys(conversionFuncs);

    return decodeURI(queryParams)
        .split('&')
        .reduce((acc, curr) => {
            try {
                const [key, value] = curr.split('=');
                const isAllowedFilter = allowedFilters.includes(key);
                const conversionToTypeFunc = conversionFuncs[key];
                const conversionedValue = conversionToTypeFunc
                    ? conversionToTypeFunc(value)
                    : value;
                const isValueAllowAndExist = isAllowedFilter && conversionedValue;

                if (conversionedValue instanceof Date && !isValid(conversionedValue)) {
                    return acc;
                }

                return isValueAllowAndExist
                    ? {
                          ...acc,
                          [key]: conversionedValue,
                      }
                    : acc;
            } catch (err) {
                Sentry.captureException(err);
                return acc;
            }
        }, {});
};

const getChangedFilters = <T>(
    prevFilters: T,
    newFilters: FiltersValues,
    coupleKeys: string[],
): FiltersValues =>
    Object.entries(newFilters).reduce((acc, [key, newFilterValue]): {
        [key: string]: unknown;
    } => {
        const isValuesNotEqualOrCoupleKey =
            prevFilters[key] !== newFilterValue || coupleKeys.includes(key);

        return isValuesNotEqualOrCoupleKey
            ? {
                  ...acc,
                  [key]: newFilterValue,
              }
            : acc;
    }, {});

export const transformFiltersToUrl = (
    filters: {
        [key: string]: unknown;
    } = {},
): string => {
    const stringedFilters = Object.entries(filters)
        .map(
            ([key, value]) =>
                `${key}=${value instanceof Date ? format(value, 'MM-dd-yyyy') : value}`,
        )
        .join('&');

    return encodeURI(stringedFilters);
};

export const removeFalsyAndExcludeFilters = <
    Filters extends Record<string, unknown>,
    ExclKeys extends (keyof Filters)[],
    FiltersWithoutExlKeys = Omit<Filters, ExclKeys[number]>,
    ReturnType = {
        [k in keyof FiltersWithoutExlKeys]: FiltersWithoutExlKeys[k] extends false
            ? never
            : FiltersWithoutExlKeys[k];
    }
>(
    filters: Filters,
    excludedKeys: ExclKeys,
): ReturnType =>
    Object.keys(filters).reduce(
        (acc, key): ReturnType => ({
            ...acc,
            // Записываем если ключ не исключен для записи и имеет не false значение
            ...(!excludedKeys.includes(key) && filters[key] && { [key]: filters[key] }),
        }),
        {} as ReturnType,
    );

/**
 * @param {object} filtersValue Объект фильтров
 * @example { offerId: null, creativeId: 1, isPersonal: true, dateFrom: new Date(), currency: 'USD' }
 * @param {object} options объект содержащий опции для фильтров
 * @property conversionFuncs - объект с функциями для преобразования данных из строки в нужный тип данных
 * @example { offerId: Number, currency: String, isPersonal: val => val ==='true', dateFrom: date => new Date(date) }
 * @property coupleKeys - массив ключей, которые могут идти парно (dateFrom, dateTo)
 * нужен при сравнений с предыдущим значением фильтров
 * @property exclude - массив ключей, которые нужно исключить
 */

type useFilterOptionsType<D> = {
    conversionFuncs: D;
    coupleKeys?: string[];
    exclude?: string[];
};

const useFilters = <T, D>(
    filtersValue: T | (() => T),
    options: useFilterOptionsType<D>,
): [T, (value: T | ((filter: T) => T)) => void] => {
    const router = useRouter();
    const { pathname, query } = router;

    const { conversionFuncs = {}, coupleKeys = [], exclude = [] } = options || {};
    const preparedFilters = filtersValue instanceof Function ? filtersValue() : filtersValue;

    const [filters, setFilters] = useState<T>(preparedFilters);

    // важно не пихать в функцию useState, чтобы не было ошибок что верстка отличается от пререндера
    useEffect((): void => {
        try {
            const filtersFromQuery = getFiltersFromQuery(conversionFuncs);
            setFilters({ ...preparedFilters, ...filtersFromQuery });
        } catch (err) {
            Sentry.captureException(err);
            // eslint-disable-next-line no-console
            console.error(err);
            setFilters(preparedFilters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setValue = (value: T) => {
        try {
            setFilters(prevFilters => {
                const newFilters = value instanceof Function ? value(filters) : value;
                const filtersFromQuery = getFiltersFromQuery(conversionFuncs);
                const changedFilters = getChangedFilters(prevFilters, newFilters, coupleKeys);

                const mergedFilters = {
                    ...filtersFromQuery,
                    ...changedFilters,
                };

                const newFiltersValue = removeFalsyAndExcludeFilters(mergedFilters, exclude);
                const urlFilters = transformFiltersToUrl(newFiltersValue);

                // TODO: в ожидании getStaticParams https://github.com/vercel/next.js/discussions/17269
                router.push(
                    { pathname, search: `?${urlFilters}` },
                    `${pathname.replace('[locale]', query.locale as string)}?${urlFilters}`,
                    { shallow: true },
                );
                return { ...filters, ...newFilters };
            });
        } catch (err) {
            Sentry.captureException(err);
            // eslint-disable-next-line no-console
            console.error(err);
            setFilters(value);
        }
    };

    return [filters, setValue];
};

/**
 * Функции для преобразования значения из строки в нужный тип
 */
export const conversionFuncs = {
    number: Number,
    string: String,
    boolean: (val: unknown): boolean => val === 'true',
    array: (val: string): unknown[] => val.split(','),
    date: (date: string | number | Date): Date => new Date(date),
};

export default useFilters;
